/**
 * This is the Accordion widget
 *
 * @prop widgetId {String} widget ID
 * @prop onSelectTab {Func} what to do when a user selects a tab
 * @prop selectedTab {String} The selected tab's ID
 * @prop tabs {Array} List of tabs
 *   expected structure is [{ id: ID, value: value, content: content }, ... ]
 *
 * optional values:
 ÷ @prop type {String}
 *   valid types are:
 *   small: widget has smaller paddings
 *   large: (default) widget has larger paddings
 *
 * return widgetID: {String} widget ID
 *        selectedElemID: {String} selected tab ID
 */
/* globals window */
import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { omit, snakeCase, throttle } from 'lodash';

import displayName from 'component-lib/displayName';
import isDeprecated from 'component-lib/isDeprecated';

import TextLabel from './Labels/TextLabel';
import PlusGrayIcon from '../Icons/PlusGrayIcon';

import styles from './Accordion.scss';

export default class Accordion extends Component {
    static displayName = displayName('Widgets/Accordion');

    static propTypes = {
        widgetId: PropTypes.string.isRequired,
        tabs: PropTypes.array.isRequired,
        onSelectTab: PropTypes.func.isRequired,
        selectedTab: PropTypes.string.isRequired,
        type: PropTypes.string,
        className: PropTypes.string,
    };

    static defaultProps = {
        className: '',
        type: 'large',
    };

    constructor(props) {
        super(props);

        isDeprecated(Accordion, 'Accordions/Accordion');

        this.innerPadding = 0;
        this.tabHeight = 45;

        this.closedTabsSelector = '[data-accordion="closed"], [data-accordion="disabled"]';

        this.throttledSetWidgetView = throttle(this.updateWidgetView, 10);
        window.addEventListener('resize', this.throttledSetWidgetView);
        $(window).on('verticalHeightChanged', this.throttledSetWidgetView);
    }

    componentDidMount() {
        window.setTimeout(this.setWidgetView, 0);
    }

    componentDidUpdate() {
        this.calculateContentHeight();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.throttledSetWidgetView);
        $(window).off('verticalHeightChanged', this.throttledSetWidgetView);
    }

    onSelectTab = (tabId, isDisabled) => () => {
        if (!isDisabled) {
            this.props.onSelectTab(this.props.widgetId, tabId);
        }
    };

    setWidgetView = () => {
        const { tabs } = this.props;
        const $widgetWrapper = $(this.widgetWrapper);
        const $innerWrapper = $widgetWrapper.children('div');
        const innerPadding = parseInt($innerWrapper.css('paddingTop'), 10) * 2;
        const innerHeight = $widgetWrapper.parent().innerHeight();

        const height = Math.max(
            innerPadding + (this.tabHeight * tabs.length),
            innerHeight,
        );

        this.innerPadding = innerPadding;

        $widgetWrapper.css({ height });

        this.calculateContentHeight();
    };

    updateWidgetView = () => {
        this.setWidgetView();

        // Since we don't have event for the resizing's end
        // This little hack recalculate the widget after the last resizing event
        window.clearTimeout(this.delayedResize);
        this.delayedResize = window.setTimeout(this.setWidgetView, 250);
    };

    calculateContentHeight() {
        const { tabs } = this.props;
        const $widgetWrapper = $(this.widgetWrapper);
        const $innerWrapper = $widgetWrapper.children('div');
        const innerWrapperHeight = $innerWrapper.height();
        const $openedTab = $widgetWrapper.find('[data-accordion="opened"]');
        const $closedTabs = $widgetWrapper.find(this.closedTabsSelector);
        const borderWidth = parseInt($closedTabs.css('borderBottomWidth') || 0, 10);
        const height =
      innerWrapperHeight - ((tabs.length - 1) * (this.tabHeight - borderWidth));

        $openedTab.css({ height });
        $closedTabs.css({ height: this.tabHeight });
    }

    renderTabs() {
        let { selectedTab } = this.props;
        const { tabs } = this.props;
        const currentSelectedTab = selectedTab;

        if (!selectedTab.length && tabs.length) {
            selectedTab = tabs[0].id || '';
        }

        return tabs.map((tab) => {
            let tabStyle = 'accordion-tab';
            const { id: tabId, value: tabValue, content: tabContent, isDisabled, errors } = tab;
            const isSelectedTab = tabId === currentSelectedTab;
            let tabStatus = 'opened';

            if (!isSelectedTab && !isDisabled) {
                tabStatus = 'closed';
            } else if (isDisabled) {
                tabStatus = 'disabled';
            }

            if (isSelectedTab && !isDisabled) {
                tabStyle += '-selected';
            } else if (isDisabled) {
                tabStyle += '-disabled';
            }

            return (
                <div
                    key={tabId}
                    role="presentation"
                    className={styles[tabStyle]}
                    data-accordion={tabStatus}
                    data-action={tabId}
                    onClick={this.onSelectTab(tabId, isDisabled)}
                >
                    <TextLabel data-field="tab-value" className={styles[`tab-value-${tabStatus}`]}>
                        {tabValue}
                        {errors &&
              !isSelectedTab && <data className="accordion-error">
                  {errors}</data>}
                    </TextLabel>

                    {!isSelectedTab && !isDisabled && <PlusGrayIcon className={styles['plus-icon']} />}

                    <div className={styles[`tab-content-${tabStatus}`]}>
                        {tabContent}
                    </div>
                </div>
            );
        });
    }

    render() {
        const widgetProps = omit(this.props, [
            'className',
            'widgetId',
            'tabs',
            'selectedTab',
            'onSelectTab',
            'children',
            'type',
            'styleName',
            'styles',
        ]);
        const { type, widgetId } = this.props;

        let className = styles['accordion-inner'];
        if (this.props.className) {
            className += ` ${this.props.className}`;
        }

        return (
            <div
                className={styles[`accordion-wrapper-${type}`]}
                ref={(c) => {
                    this.widgetWrapper = c;
                }}
            >
                <div {...widgetProps} data-field={`accordion-${snakeCase(widgetId)}`} className={className}>
                    {this.renderTabs()}
                </div>
            </div>
        );
    }
}
